/** Norn1 content commitment adapter; Rust-produced fixtures are the authority.
 * WebCrypto owns SHA-256; the existing lossless-json dependency owns exact JSON
 * token parsing and persistence on the SDK's ES2022 browser baseline.
 */
import { LosslessNumber, parse, stringify } from "lossless-json"
import type { components } from "./generated/norn-api.ts"
type Payload = components["schemas"]["SlicePayload"]

/** Parse wire frames or persisted pages without rounding exact JSON decimals. */
export function parseSubscriptionJson(text: string): unknown {
  // Prefix every key before the pinned parser constructs objects: its released
  // API has no safe object factory. Native JSON.parse decodes key strings;
  // Object.fromEntries restores them as own data properties, including __proto__.
  // Matching all string tokens keeps key-like text inside string values intact.
  const keyed = text.replace(/"(?:[^"\\]|\\.)*"(\s*:)?/g, (token, colon: string | undefined) => {
    if (colon === undefined) return token
    const key = JSON.parse(token.slice(0, -colon.length)) as string
    return `${JSON.stringify(`$${key}`)}${colon}`
  })
  const parsed = parse(keyed, null, (source: string) => {
    const value = Number(source)
    const exact = canonicalNumber(source)
    if (exact === "0") return 0 // Rust's admitted decimal has one canonical zero.
    return Number.isFinite(value) && canonicalNumber(String(value)) === exact
      ? value : new LosslessNumber(source)
  })
  function restore(value: unknown): unknown {
    if (value instanceof LosslessNumber) return value
    if (Array.isArray(value)) return value.map(restore)
    if (typeof value === "object" && value !== null) {
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key.slice(1), restore(item)]))
    }
    return value
  }
  return restore(parsed)
}

/** Persist a page without losing its exact numeric tokens. */
export function stringifySubscriptionJson(value: unknown): string {
  // The library's public marker is also a valid ordinary JSON property.
  // Its supported replacer shields such objects from numeric dispatch while
  // restoring the original marker when serializing their properties.
  const markers = new WeakMap<object, unknown>()
  const text = stringify(value, function (this: object, key: string, item: unknown) {
    if (key === "isLosslessNumber" && markers.has(this)) item = markers.get(this)
    if (typeof item === "object" && item !== null && !(item instanceof LosslessNumber)
      && "isLosslessNumber" in item && item.isLosslessNumber) {
      const safe = Array.isArray(item) ? [...item] : { ...item, isLosslessNumber: false }
      markers.set(safe, item.isLosslessNumber)
      return safe
    }
    return item
  })
  if (text === undefined) throw new Error("non-JSON subscription state")
  return text
}

function canonicalNumber(source: string, admit = false): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(source)
  if (!match) throw new Error("invalid JSON number")
  const fraction = match[3] ?? ""
  let digits = `${match[2]}${fraction}`.replace(/^0+/, "")
  if (digits === "") return "0"
  let scale = fraction.length - Number(match[4] ?? 0)
  if (!Number.isSafeInteger(scale) || Math.abs(scale) + digits.length > 1_048_576) throw new Error("number exceeds Norn1 bound")
  while (digits.endsWith("0")) { digits = digits.slice(0, -1); scale -= 1 }
  if (admit && (scale > 28 || digits.length + Math.max(0, -scale) > 29
    || BigInt(digits + "0".repeat(Math.max(0, -scale))) > 79228162514264337593543950335n)) {
    throw new Error("number exceeds Rust decimal domain")
  }
  const magnitude = scale <= 0 ? digits + "0".repeat(-scale)
    : scale >= digits.length ? `0.${"0".repeat(scale - digits.length)}${digits}`
    : `${digits.slice(0, digits.length - scale)}.${digits.slice(digits.length - scale)}`
  return `${match[1]}${magnitude}`
}

function scalarOrder(left: string, right: string): number {
  const a = Array.from(left, (char) => char.codePointAt(0)!)
  const b = Array.from(right, (char) => char.codePointAt(0)!)
  for (let i = 0; i < Math.min(a.length, b.length); i += 1) {
    if (a[i] !== b[i]) return a[i]! - b[i]!
  }
  return a.length - b.length
}

function canonical(value: unknown, depth = 0): string {
  if (depth > 256) throw new Error("Norn1 nesting bound exceeded")
  if (value === null || typeof value === "boolean") return JSON.stringify(value)
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("nonfinite JSON number")
    return canonicalNumber(String(value), true)
  }
  if (typeof value === "string") {
    // Unicode-mode matching treats a valid surrogate pair as one astral scalar.
    if (/[\uD800-\uDFFF]/u.test(value)) throw new Error("invalid Unicode scalar")
    return JSON.stringify(value.normalize("NFC"))
  }
  if (value instanceof LosslessNumber) return canonicalNumber(value.value, true)
  if (Array.isArray(value)) return `[${value.map((item) => canonical(item, depth + 1)).join(",")}]`
  if (typeof value === "object" && value !== null) {
    if (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) throw new Error("non-JSON object")
    const entries = Object.entries(value).map(([key, item]) => [key.normalize("NFC"), item] as const)
    entries.sort(([a], [b]) => scalarOrder(a, b))
    if (entries.some(([key], i) => i > 0 && entries[i - 1]![0] === key)) throw new Error("NFC key collision")
    return `{${entries.map(([key, item]) => `${canonical(key)}:${canonical(item, depth + 1)}`).join(",")}}`
  }
  throw new Error("non-JSON value")
}

export async function matchesContent(payload: Payload, cursor: string): Promise<boolean> {
  try {
    const identities = new Set(payload.items.map((item) => JSON.stringify([item.objectType, item.key])))
    if (identities.size !== payload.items.length) return false
    const bytes = new TextEncoder().encode(`norn.subscription.result.v1\0${canonical([payload.items, payload.complete])}`)
    const digest = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", bytes))
    const hex = Array.from(digest.slice(0, 8), (byte) => byte.toString(16).padStart(2, "0")).join("")
    return cursor.split(".")[5] === hex
  } catch {
    return false
  }
}
